import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { Connectable, TConnectableProps } from './Connectable.hoc';

import View from '../../components/View';
import Translator from '../../helpers/TranslationHelper';
import RegistersTab from './RegistersTab';

import {
    Employees,
    FuelTanks,
    RFIDCards,
    Vehicles,
} from '../../components/registers';

const EMPLOYEES = 'employees_tab';
const VEHICLES = 'vehicles_tab';
const CARDS = 'rfids_tab';
const TANKS = 'fuel_tanks_tab';

const TAB_URL_MAP: Record<string, string> = {
    [VEHICLES]: 'vehicles',
    [EMPLOYEES]: 'employees',
    [CARDS]: 'rfid-cards',
    [TANKS]: 'tanks',
};

const URL_TAB_MAP: Record<string, string> = Object.keys(TAB_URL_MAP).reduce(
    (acc, key) => {
        acc[TAB_URL_MAP[key]] = key;
        return acc;
    },
    {} as Record<string, string>
);

type TProps = TConnectableProps & RouteComponentProps<{ tab?: string }>;

class RegistersPage extends React.Component<TProps> {
    public componentDidMount() {
        this.syncUrlToRedux();
    }

    public componentDidUpdate(prevProps: TProps) {
        if (
            this.props.match.params.tab !== prevProps.match.params.tab ||
            this.props.location.search !== prevProps.location.search
        ) {
            this.syncUrlToRedux();
        }

        const activeTab = this.getActiveTab();
        const prevActiveTab = this.getPrevActiveTab(prevProps);

        if (activeTab === prevActiveTab) {
            this.syncReduxToUrl(prevProps);
        }
    }

    private getPrevActiveTab(prevProps: TProps) {
        const { tab } = prevProps.match.params;
        const mappedTab = tab ? URL_TAB_MAP[tab] : null;
        const visibleTabs = this.getVisibleTabsForProps(prevProps);

        if (mappedTab && visibleTabs.includes(mappedTab)) {
            return mappedTab;
        }

        const tabs = this.getVisibleTabsForProps(prevProps);
        return tabs.length > 0 ? tabs[0] : null;
    }

    private getVisibleTabsForProps(props: TProps) {
        const tabs = [];
        if (props.hasAccessToVehicles) {
            tabs.push(VEHICLES);
        }
        if (props.hasAccessToEmployees) {
            tabs.push(EMPLOYEES);
        }
        if (props.hasAccessToCards) {
            tabs.push(CARDS);
        }
        if (props.hasAccessToTanks) {
            tabs.push(TANKS);
        }
        return tabs;
    }

    private syncUrlToRedux() {
        const urlParams = new URLSearchParams(this.props.location.search);
        const search = urlParams.get('search') || '';
        const group = urlParams.get('group') || undefined;
        const activeTab = this.getActiveTab();

        switch (activeTab) {
            case VEHICLES:
                if (
                    this.props.vehiclesFilter.name !== search ||
                    this.props.vehiclesFilter.group !== group
                ) {
                    this.props.setVehiclesFilter({
                        vehiclesFilter: {
                            ...this.props.vehiclesFilter,
                            name: search,
                            group: group,
                            start: 0,
                        },
                    });
                }
                break;
            case EMPLOYEES:
                if (
                    this.props.employeesFilter.name !== search ||
                    this.props.employeesFilter.group !== group
                ) {
                    this.props.setEmployeesFilter({
                        employeesFilter: {
                            ...this.props.employeesFilter,
                            name: search,
                            group: group,
                            start: 0,
                        },
                    });
                }
                break;
            case CARDS:
                if (
                    this.props.rfidCardsFilter.name !== search ||
                    this.props.rfidCardsFilter.group !== group
                ) {
                    this.props.setRfidCardsFilter({
                        rfidCardsFilter: {
                            ...this.props.rfidCardsFilter,
                            name: search,
                            group: group,
                            start: 0,
                        },
                    });
                }
                break;
            case TANKS:
                if (
                    this.props.fuelTanksFilter.name !== search ||
                    this.props.fuelTanksFilter.group !== group
                ) {
                    this.props.setFuelTanksFilter({
                        fuelTanksFilter: {
                            ...this.props.fuelTanksFilter,
                            name: search,
                            group: group,
                            start: 0,
                        },
                    });
                }
                break;
        }
    }

    private syncReduxToUrl(prevProps: TProps) {
        const activeTab = this.getActiveTab();
        let currentSearch = '';
        let prevSearch = '';
        let currentGroup: string | undefined = undefined;
        let prevGroup: string | undefined = undefined;

        switch (activeTab) {
            case VEHICLES:
                currentSearch = this.props.vehiclesFilter.name || '';
                prevSearch = prevProps.vehiclesFilter.name || '';
                currentGroup = this.props.vehiclesFilter.group;
                prevGroup = prevProps.vehiclesFilter.group;
                break;
            case EMPLOYEES:
                currentSearch = this.props.employeesFilter.name || '';
                prevSearch = prevProps.employeesFilter.name || '';
                currentGroup = this.props.employeesFilter.group;
                prevGroup = prevProps.employeesFilter.group;
                break;
            case CARDS:
                currentSearch = this.props.rfidCardsFilter.name || '';
                prevSearch = prevProps.rfidCardsFilter.name || '';
                currentGroup = this.props.rfidCardsFilter.group;
                prevGroup = prevProps.rfidCardsFilter.group;
                break;
            case TANKS:
                currentSearch = this.props.fuelTanksFilter.name || '';
                prevSearch = prevProps.fuelTanksFilter.name || '';
                currentGroup = this.props.fuelTanksFilter.group;
                prevGroup = prevProps.fuelTanksFilter.group;
                break;
        }

        if (currentSearch !== prevSearch || currentGroup !== prevGroup) {
            const urlParams = new URLSearchParams(this.props.location.search);
            if (currentSearch) {
                urlParams.set('search', currentSearch);
            } else {
                urlParams.delete('search');
            }

            if (currentGroup) {
                urlParams.set('group', currentGroup);
            } else {
                urlParams.delete('group');
            }

            const newSearch = urlParams.toString();
            const currentPath = this.props.location.pathname;
            this.props.history.push({
                pathname: currentPath,
                search: newSearch ? `?${newSearch}` : '',
            });
        }
    }

    public getVisibleTabs() {
        const tabs = [];
        if (this.props.hasAccessToVehicles) {
            tabs.push(VEHICLES);
        }
        if (this.props.hasAccessToEmployees) {
            tabs.push(EMPLOYEES);
        }
        if (this.props.hasAccessToCards) {
            tabs.push(CARDS);
        }
        if (this.props.hasAccessToTanks) {
            tabs.push(TANKS);
        }
        return tabs;
    }

    public getActiveTab() {
        const { tab } = this.props.match.params;
        const mappedTab = tab ? URL_TAB_MAP[tab] : null;
        const visibleTabs = this.getVisibleTabs();

        if (mappedTab && visibleTabs.includes(mappedTab)) {
            return mappedTab;
        }

        return this.getInitTab();
    }

    public getInitTab() {
        const tabs = this.getVisibleTabs();
        return tabs.length > 0 ? tabs[0] : null;
    }

    public handleTabClick = (tab: string) => {
        const urlPart = TAB_URL_MAP[tab];
        if (urlPart) {
            const urlParams = new URLSearchParams(this.props.location.search);
            urlParams.delete('search');
            urlParams.delete('group');
            const search = urlParams.toString();
            this.props.history.push({
                pathname: `/registers/${urlPart}`,
                search: search ? `?${search}` : '',
            });
        }
    };

    public _renderTabs() {
        const tabs = this.getVisibleTabs();
        const activeTab = this.getActiveTab();
        return tabs.map((tab, index) => {
            const buttonClass = tab === activeTab ? 'selected' : '';
            return (
                <RegistersTab
                    key={index}
                    id={index}
                    buttonClass={buttonClass}
                    handleClick={this.handleTabClick}
                    tabName={tab}
                />
            );
        });
    }

    public _renderTabContent() {
        let content;
        const activeTab = this.getActiveTab();
        switch (activeTab) {
            case VEHICLES: {
                content = this.props.hasAccessToVehicles && <Vehicles />;
                break;
            }
            case EMPLOYEES: {
                content = this.props.hasAccessToEmployees && <Employees />;
                break;
            }
            case CARDS: {
                content = this.props.hasAccessToCards && <RFIDCards />;
                break;
            }
            case TANKS: {
                content = this.props.hasAccessToTanks && <FuelTanks />;
                break;
            }
            default: {
                content = null;
                break;
            }
        }
        return content;
    }

    public render() {
        return (
            <View title={Translator.translate('Registers')}>
                <div
                    className="page-content--main-component"
                    id="registersPageContent"
                >
                    <div className="page-block">
                        <div className="tabs">
                            <ul className="tabs-header">
                                {this._renderTabs()}
                            </ul>
                        </div>

                        <div className="registers-ui">
                            {this._renderTabContent()}
                        </div>
                    </div>
                </div>
            </View>
        );
    }
}

export default withRouter(Connectable(RegistersPage));
